import map from 'lodash/map';

// TECH-3934 (PFB-261): optional per-formula minimum number of answered questions.
// "Answered" means the answer evaluates to a number (decision D3), so skipped,
// empty and non-numeric answers do not count. Below the threshold the formula
// returns Formula.InsufficientData and no value, never a partial score.

// D6: stable marker on the Formula.InsufficientData error. Displays and exports match
// on it (utils/insufficientData.js); the message stays free text.
export const ANSWER_STATUS__INSUFFICIENT_DATA = 'insufficient_data';

// QSC H1: a threshold can arrive as text (a typed "20", or an import). A whole-number
// string counts as that number. Any other set value is a configuration error and must
// never fall back to a score. Unset (undefined, null, a blank string) means no minimum.
// The schema admits strings; isValidMinAnswered (in validate) rejects the bad ones.
export const minAnsweredSchema = {
  type: ['integer', 'string', 'null'],
  minimum: 0,
};

const WHOLE_NUMBER = /^\d+$/;

// Form Builder stores the minimum as typed (no parser), untrimmed, so "20 " is 20
// and "  " is unset, as they were when the field parsed its text as JSON.
const isUnsetMinAnswered = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim() === '');

/**
 * The threshold as a number, or undefined when unset or not a whole number >= 0.
 * @param {*} value
 * @returns {number|undefined}
 */
const toMinAnswered = (value) => {
  if (Number.isSafeInteger(value) && value >= 0) {
    return value;
  }
  if (typeof value === 'string' && WHOLE_NUMBER.test(value.trim())) {
    // Only a safe integer is a minimum. Above 2^53 - 1 the number is rounded (not the
    // count typed), and past ~1.8e308 it is Infinity, which JSON (the published
    // data.json) writes as null - no minimum. Either way: not configured.
    const number = Number(value.trim());
    return Number.isSafeInteger(number) ? number : undefined;
  }
  return undefined;
};

/**
 * True when minAnswered is unset or a whole number >= 0 (number or string).
 * @param {Object} [settings]
 * @returns {boolean}
 */
export const isValidMinAnswered = (settings) =>
  !settings ||
  isUnsetMinAnswered(settings.minAnswered) ||
  toMinAnswered(settings.minAnswered) !== undefined;

/**
 * Copy the threshold an author set in Form Builder (meta) into compiled settings.
 * A valid value is stored as a number. An invalid value is copied as it is, so that
 * validate() reports the formula as not configured instead of returning a score.
 * @param {Object} settings
 * @param {Object} [meta]
 * @returns {Object}
 */
export const withMinAnswered = (settings, meta) => {
  if (!meta || isUnsetMinAnswered(meta.minAnswered)) {
    return settings;
  }
  const minAnswered = toMinAnswered(meta.minAnswered);
  return {
    ...settings,
    minAnswered: minAnswered === undefined ? meta.minAnswered : minAnswered,
  };
};

const hasMinAnswered = (source) =>
  !!source && toMinAnswered(source.minAnswered) !== undefined;

/**
 * @param {Object} settings
 * @param {number} nAnswered
 * @returns {boolean}
 */
export const isBelowMinAnswered = (settings, nAnswered) =>
  hasMinAnswered(settings) && nAnswered < toMinAnswered(settings.minAnswered);

// MongoDB versions of the rules above, for analytics charts (toMongoExpression).
// In a stored response the numeric value of an answer is `meta.number`, the same
// field the chart sum and average already read.
const MONGO_NUMBER_TYPES = ['double', 'int', 'long', 'decimal'];

/**
 * Mongo expression: the number of responses to the given questions that carry a
 * numeric value. Zero is a value.
 * @param {Array<{ id: string }>} operands
 * @returns {Object}
 */
// review:manual: Question.createResponse (Question.js) sets meta.number only for string and number answers, so a yes/no answer (a boolean) has none and is not counted here, although evaluate() counts it as 1 or 0 (Darryl, 2026-10-29). A chart can therefore show no value where the form shows a score. Counting booleans needs meta.number for them, which changes every stored response, so it is left for a separate change.
export const createCountAnsweredExpression = (operands) => ({
  $size: {
    $filter: {
      input: { $ifNull: ['$responses', []] },
      cond: {
        $and: [
          {
            $in: ['$$this.questionId', { $literal: map(operands, 'id') }],
          },
          {
            $in: [
              { $type: '$$this.meta.number' },
              { $literal: MONGO_NUMBER_TYPES },
            ],
          },
        ],
      },
    },
  },
});

/**
 * Wrap a chart value expression so that it is null below the threshold.
 * Without a threshold the expression is returned unchanged.
 * @param {Object} settings
 * @param {Array<{ id: string }>} operands
 * @param {Object} expression
 * @returns {Object}
 */
export const withMinAnsweredExpression = (settings, operands, expression) =>
  hasMinAnswered(settings)
    ? {
        $cond: {
          if: {
            $lt: [
              createCountAnsweredExpression(operands),
              { $literal: toMinAnswered(settings.minAnswered) },
            ],
          },
          then: null,
          else: expression,
        },
      }
    : expression;
